import React from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingDown,
  TrendingUp,
  Target,
  AlertTriangle,
  BarChart3,
  Shield,
  Clock,
  ExternalLink,
  ChevronRight,
  Eye,
  EyeOff,
  Building,
  Users
} from 'lucide-react';
import SeoHead from '../seo/SeoHead';
import { staticPageMeta } from '../seo/metadata';
import { breadcrumbSchema } from '../seo/structuredData';

const INTER_STACK = "'Inter', system-ui, -apple-system, sans-serif";

const SECTRACE_WORDMARK = {
  fontFamily: "'Space Mono', 'Courier New', monospace",
  fontWeight: 700,
  letterSpacing: '-0.03em'
};

const headingStyle = {
  fontFamily: INTER_STACK,
  fontWeight: 200,
  letterSpacing: '-0.02em'
};

const bodyStyle = {
  fontFamily: INTER_STACK,
  fontWeight: 300
};

const ctaStyle = {
  fontFamily: INTER_STACK,
  fontWeight: 500,
  letterSpacing: '0.18em',
  fontSize: '0.7rem'
};

const labelStyle = {
  ...bodyStyle,
  fontSize: '0.65rem',
  letterSpacing: '0.2em'
};

const SecTraceResearch = () => {
  const requesterTypes = [
    {
      tone: 'negative',
      title: 'Negative Return Signals',
      icon: TrendingDown,
      items: [
        {
          icon: Target,
          label: 'Probes Requests',
          value: '-2.4%',
          desc: 'Investigation-related requests from firms like Probes Reporter predict negative returns.'
        },
        {
          icon: EyeOff,
          label: 'Anonymous Requests',
          value: '-3.4%',
          desc: 'Hidden requesters (often short sellers) signal negative future performance.'
        },
        {
          icon: AlertTriangle,
          label: 'B7A Exemption Denials',
          value: '-5.9%',
          desc: 'Requests denied due to "ongoing enforcement" predict SEC investigations.'
        }
      ]
    },
    {
      tone: 'positive',
      title: 'Positive Return Signals',
      icon: TrendingUp,
      items: [
        {
          icon: Building,
          label: 'Investment Firms',
          value: '+9.2%',
          desc: 'Institutional investors filing FOIA requests signal long-position interest.'
        },
        {
          icon: Shield,
          label: 'IP/Patent Requests',
          value: '+3.7%',
          desc: 'Intellectual property firms seeking innovation data predict growth.'
        },
        {
          icon: Users,
          label: 'Compliance & Legal',
          value: 'No signal',
          desc: 'Law firm requests show no significant return predictability.',
          neutral: true
        }
      ]
    }
  ];

  const b7aStats = [
    {
      value: '4×',
      title: 'Investigation Probability',
      desc: 'B7A denials have 40.1% probability of indicating an SEC investigation vs 9.0% baseline.'
    },
    {
      value: '-5.9%',
      title: '2-Year Returns',
      desc: 'Stocks with B7A denials significantly underperform over 2 years.'
    },
    {
      value: '-12.9%',
      title: 'With AAER',
      desc: 'Returns amplified when followed by enforcement releases.'
    }
  ];

  const b7aFlow = [
    { n: '1', title: 'FOIA Request', desc: 'Investor requests investigation records.' },
    { n: '2', title: 'SEC Review', desc: 'Checks internal investigation database.' },
    { n: '3', title: 'B7A Denial', desc: '"Interferes with enforcement."', highlight: true }
  ];

  const shortSellerInsights = [
    {
      icon: TrendingDown,
      title: 'Increased Short Interest',
      eyebrow: 'Following Probes & Hidden requests',
      desc: 'Short sellers show heightened activity following FOIA requests from investigation-focused requesters and anonymous filers, building positions before negative news becomes public.'
    },
    {
      icon: Eye,
      title: 'Short Reports Cite FOIA',
      eyebrow: 'Activist short sellers use SEC data',
      desc: 'Among 1,389 short reports analyzed, those citing FOIA-obtained information showed higher pre-publication short interest and more negative returns around publication.'
    }
  ];

  const intelStats = [
    { value: '67%', label: 'Of SEC FOIA requests target specific companies' },
    { value: '3,000+', label: 'Companies mentioned in requests per 6-month period' },
    { value: '26%', label: 'Of requesters hide their organization identity' }
  ];

  const elevatedActivity = [
    { company: 'FTX Trading', requests: 28, context: 'Requests surged before bankruptcy filing shook crypto markets.' },
    { company: 'Ethereum', requests: 26, context: 'Activity tracked with crypto market decline.' },
    { company: 'Signature Bank', requests: 18, context: 'Elevated requests preceded SVB-related bank run.' },
    { company: 'Coinbase', requests: 14, context: 'Linked to Signature Bank exposure concerns.' },
    { company: 'First Citizens Bank', requests: 10, context: 'Interest following SVB deposit acquisition.' },
    { company: 'GameStop', requests: 10, context: 'Sustained attention amid ongoing volatility.' }
  ];

  const requesterMix = [
    { type: 'Commercial Organizations', pct: '26%', desc: 'Research firms, consulting firms, businesses.' },
    { type: 'Investigation Services', pct: '26%', desc: 'Probes Reporter and similar services.' },
    { type: 'Educational Institutions', pct: '8%', desc: 'University students, faculty, researchers.' },
    { type: 'News Media', pct: '5%', desc: 'WSJ, Bloomberg, NYT, and other journalists.' },
    { type: 'Law Firms', pct: '4%', desc: 'Over 100 different law firms submit requests.' },
    { type: 'Financial Institutions', pct: '2%', desc: 'Investment firms, hedge funds, banks.' },
    { type: 'Political Organizations', pct: '1%', desc: 'Opposition research, policy analysis.' }
  ];

  const useCases = [
    {
      icon: TrendingDown,
      title: 'Short Selling Signals',
      description: 'Track Probes and anonymous requests to identify potential short opportunities before negative news.'
    },
    {
      icon: Shield,
      title: 'Portfolio Risk Management',
      description: 'Monitor portfolio holdings for unusual FOIA activity that may indicate undisclosed investigations.'
    },
    {
      icon: Clock,
      title: 'Early Warning System',
      description: 'B7A denials often precede enforcement actions by months or years, providing lead time.'
    },
    {
      icon: Target,
      title: 'Due Diligence',
      description: 'Screen M&A targets and new investments for regulatory risk using FOIA request patterns.'
    },
    {
      icon: TrendingUp,
      title: 'Long Ideas',
      description: 'Investment firm FOIA requests signal institutional interest and predict +9.2% returns.'
    },
    {
      icon: BarChart3,
      title: 'Quantitative Signals',
      description: 'Incorporate requester-type analysis into systematic strategies for uncorrelated alpha.'
    }
  ];

  const papers = [
    {
      n: '1',
      title:
        '"The Information Content of Private Information Acquisition: Evidence from FOIA Requests to the SEC"',
      authors: 'Bao, D., Brendel, J., Drake, M., & Su, L. (2025) — Hong Kong University / BYU',
      summary:
        'Probes requests predict -2.4% returns, anonymous requests predict -3.4%, while investment firm requests predict +9.2% and IP requests predict +3.7%.',
      url: 'https://ssrn.com/abstract=5271571'
    },
    {
      n: '2',
      title: '"Does FOIA Foil the SEC\'s Intent to Keep Investigations Confidential?"',
      authors: 'Coleman, B., Merkley, K., Miller, B., & Pacelli, J. (2020) — Indiana University',
      summary:
        'B7A exemption denials predict SEC investigations with 4× higher probability and generate -5.9% abnormal returns over 2 years.',
      url: 'https://ssrn.com/abstract=3545838'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: INTER_STACK }}>
      <SeoHead
        {...staticPageMeta({
          title: 'SEC Trace Research',
          description: 'SEC Trace research on FOIA requests and regulatory investigations.',
          path: '/sectrace/research',
          ogImage: `${process.env.REACT_APP_SITE_URL || 'https://researchphalanx.com'}/sectrace-logo.png`,
        })}
        jsonLd={breadcrumbSchema([
          { name: 'Phalanx', path: '/' },
          { name: 'SEC Trace', path: '/sectrace' },
          { name: 'Research', path: '/sectrace/research' },
        ])}
      />
      {/* Header — transparent, floats over the hero. Mirrors SEC Trace landing. */}
      <header className="absolute top-0 left-0 right-0 z-30 px-6">
        <div className="max-w-6xl mx-auto py-5 sm:py-7">
          <nav className="flex items-center justify-between gap-4">
            <div className="flex items-baseline gap-5 sm:gap-6">
              <Link
                to="/sectrace"
                className="text-white/70 hover:text-white transition-colors"
                style={{
                  ...bodyStyle,
                  fontSize: '0.8rem',
                  letterSpacing: '-0.01em',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                ← SEC Trace
              </Link>
              <span
                className="text-white leading-none"
                style={{
                  ...SECTRACE_WORDMARK,
                  fontSize: 'clamp(1.25rem, 3vw, 2.25rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                Research
              </span>
            </div>
            <div
              className="flex items-center gap-4 sm:gap-6"
              style={{
                ...bodyStyle,
                fontSize: '0.85rem',
                letterSpacing: '-0.01em',
                textShadow: '0 2px 20px rgba(0,0,0,0.3)'
              }}
            >
              <Link to="/sectrace#search" className="text-white/70 hover:text-white hidden sm:inline transition-colors">
                Search
              </Link>
              <Link to="/sectrace#alerts" className="text-white/70 hover:text-white hidden sm:inline transition-colors">
                Alerts
              </Link>
              <span className="text-white">Research</span>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero — full viewport dark panel matching the SEC Trace landing aesthetic */}
      <section className="relative h-screen overflow-hidden bg-gray-900">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 30%, rgba(60,64,72,0.55) 0%, rgba(17,24,39,0.25) 45%, rgba(0,0,0,0.6) 100%)'
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)',
            backgroundSize: '28px 28px'
          }}
        />

        <div className="relative h-full flex flex-col">
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-center max-w-4xl">
              <div
                className="text-white/55 uppercase mb-5"
                style={{ ...labelStyle, letterSpacing: '0.28em' }}
              >
                Peer-Reviewed Academic Research
              </div>
              <h1
                className="text-white mb-6 leading-[1.15]"
                style={{
                  ...headingStyle,
                  fontSize: 'clamp(1.75rem, 4.2vw, 3rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                SEC FOIA requests
                <br />
                <span className="text-white/55">predict stock returns.</span>
              </h1>
              <p
                className="text-white/80 leading-relaxed max-w-2xl mx-auto"
                style={{
                  ...bodyStyle,
                  fontSize: 'clamp(0.95rem, 1.6vw, 1.15rem)',
                  textShadow: '0 1px 12px rgba(0,0,0,0.3)'
                }}
              >
                Multiple academic studies demonstrate that SEC FOIA request patterns
                predict future stock performance, with different requester types signaling
                positive or negative returns.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 mt-8 sm:mt-10">
                <a
                  href="#findings"
                  className="inline-flex items-center gap-2 text-white border-b border-white hover:border-gray-300 hover:text-gray-300 pb-0.5 uppercase transition-colors"
                  style={ctaStyle}
                >
                  Read The Findings
                  <span className="text-base">→</span>
                </a>
                <Link
                  to="/sectrace"
                  className="inline-flex items-center gap-2 text-white/70 border-b border-white/40 hover:border-white hover:text-white pb-0.5 uppercase transition-colors"
                  style={ctaStyle}
                >
                  Search The Database
                  <span className="text-base">→</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Headline study stats overlaid at the bottom of the hero */}
          <div className="pb-10 sm:pb-14 px-6">
            <div className="max-w-5xl mx-auto grid grid-cols-3 gap-3 sm:gap-8 text-center">
              {[
                { value: '+9.2%', label: 'Investment Firm Signal' },
                { value: '-5.9%', label: 'B7A Denial Signal' },
                { value: '4×', label: 'Investigation Probability' }
              ].map((s) => (
                <div key={s.label} className="px-2 sm:px-4">
                  <div
                    className="text-white mb-1.5 sm:mb-2"
                    style={{
                      ...headingStyle,
                      fontSize: 'clamp(1.5rem, 3.6vw, 2.75rem)',
                      textShadow: '0 2px 20px rgba(0,0,0,0.35)'
                    }}
                  >
                    {s.value}
                  </div>
                  <div
                    className="text-white/60 uppercase"
                    style={{
                      ...bodyStyle,
                      fontSize: '0.65rem',
                      letterSpacing: '0.2em'
                    }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Requester Types — split panel */}
      <section id="findings" className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-12 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              Findings
            </div>
            <h2
              className="text-gray-900 leading-[1.2] mb-3"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              Returns predicted by requester type.
            </h2>
            <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '1rem' }}>
              Different types of FOIA requesters signal distinct stock performance outcomes
              over the following 12 months.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-3 sm:gap-5">
            {requesterTypes.map((group) => {
              const HeaderIcon = group.icon;
              const accent =
                group.tone === 'negative' ? 'text-red-600' : 'text-green-600';
              return (
                <div
                  key={group.title}
                  className="rounded-xl border border-gray-200 bg-[#FAF8F3] overflow-hidden"
                >
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
                    <HeaderIcon className={`w-4 h-4 ${accent}`} />
                    <span
                      className="text-gray-900 uppercase"
                      style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.75rem', letterSpacing: '0.16em' }}
                    >
                      {group.title}
                    </span>
                  </div>
                  <div className="p-6 space-y-5">
                    {group.items.map((item) => {
                      const ItemIcon = item.icon;
                      const valueClass = item.neutral
                        ? 'text-gray-400'
                        : group.tone === 'negative'
                          ? 'text-red-600'
                          : 'text-green-600';
                      return (
                        <div key={item.label} className="flex items-start gap-4">
                          <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                            <ItemIcon className={`w-4 h-4 ${item.neutral ? 'text-gray-400' : group.tone === 'negative' ? 'text-red-600' : 'text-green-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-3">
                              <span
                                className="text-gray-900"
                                style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.9rem' }}
                              >
                                {item.label}
                              </span>
                              <span
                                className={valueClass}
                                style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.85rem' }}
                              >
                                {item.value}
                              </span>
                            </div>
                            <p
                              className="text-gray-600 mt-1 leading-relaxed"
                              style={{ ...bodyStyle, fontSize: '0.8rem' }}
                            >
                              {item.desc}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <p
            className="text-gray-400 mt-6 text-center"
            style={{ ...bodyStyle, fontSize: '0.72rem' }}
          >
            Returns shown are for a 2× increase in FOIA request volume. Source: Bao, Brendel, Drake &amp; Su (2025).
          </p>
        </div>
      </section>

      {/* B7A Deep Dive */}
      <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-12 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              B7A Exemptions
            </div>
            <h2
              className="text-gray-900 leading-[1.2] mb-3"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              The investigation signal.
            </h2>
            <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '1rem' }}>
              When the SEC denies a FOIA request citing &ldquo;interference with enforcement
              proceedings&rdquo;, it is one of the strongest leading indicators of an
              ongoing investigation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-3 sm:gap-5 mb-10">
            {b7aStats.map((stat) => (
              <div
                key={stat.title}
                className="rounded-xl border border-gray-200 bg-[#FAF8F3] p-7"
              >
                <div
                  className="text-gray-900 mb-3"
                  style={{ ...headingStyle, fontSize: 'clamp(2rem, 3.2vw, 2.75rem)' }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-gray-900 mb-2"
                  style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.95rem' }}
                >
                  {stat.title}
                </div>
                <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                  {stat.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Process flow */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="uppercase text-gray-500" style={labelStyle}>
                How a B7A signal forms
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-center gap-3 md:gap-4">
              {b7aFlow.map((step, idx) => (
                <React.Fragment key={step.n}>
                  <div
                    className={`flex-1 max-w-xs mx-auto md:mx-0 rounded-xl p-5 text-center ${
                      step.highlight
                        ? 'bg-gray-900 border border-gray-900'
                        : 'bg-[#FAF8F3] border border-gray-200'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 mx-auto mb-3 rounded-full flex items-center justify-center ${
                        step.highlight ? 'bg-white text-gray-900' : 'bg-white border border-gray-200 text-gray-700'
                      }`}
                      style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.8rem' }}
                    >
                      {step.highlight ? <AlertTriangle className="w-4 h-4" /> : step.n}
                    </div>
                    <div
                      className={`mb-1 ${step.highlight ? 'text-white' : 'text-gray-900'}`}
                      style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.9rem' }}
                    >
                      {step.title}
                    </div>
                    <p
                      className={`leading-relaxed ${step.highlight ? 'text-gray-300' : 'text-gray-500'}`}
                      style={{ ...bodyStyle, fontSize: '0.78rem' }}
                    >
                      {step.desc}
                    </p>
                  </div>
                  {idx < b7aFlow.length - 1 && (
                    <ChevronRight className="w-5 h-5 text-gray-300 hidden md:block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Short seller activity */}
      <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-12 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              Short Sellers
            </div>
            <h2
              className="text-gray-900 leading-[1.2] mb-3"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              Short sellers trade on FOIA data.
            </h2>
            <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '1rem' }}>
              Sophisticated investors are already using FOIA requests to inform trading
              decisions, often weeks before the underlying news becomes public.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-3 sm:gap-5">
            {shortSellerInsights.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="group rounded-xl border border-gray-200 bg-[#FAF8F3] hover:bg-gray-900 hover:border-gray-900 p-6 sm:p-7 cursor-default"
                  style={{
                    transition:
                      'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-11 h-11 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-700 group-hover:bg-gray-800 group-hover:border-gray-700 group-hover:text-white"
                      style={{
                        transition:
                          'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1), color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                      }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div
                        className="text-gray-900 group-hover:text-white"
                        style={{
                          ...headingStyle,
                          fontWeight: 400,
                          fontSize: '1.05rem',
                          letterSpacing: '-0.01em',
                          transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                        }}
                      >
                        {item.title}
                      </div>
                      <div
                        className="text-gray-500 group-hover:text-gray-400 mt-0.5"
                        style={{
                          ...bodyStyle,
                          fontSize: '0.72rem',
                          transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                        }}
                      >
                        {item.eyebrow}
                      </div>
                    </div>
                  </div>
                  <p
                    className="text-gray-600 group-hover:text-gray-300 leading-relaxed"
                    style={{
                      ...bodyStyle,
                      fontSize: '0.9rem',
                      transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                    }}
                  >
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Market intelligence */}
      <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-12 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              Real-World Activity
            </div>
            <h2
              className="text-gray-900 leading-[1.2] mb-3"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              FOIA requests as market intelligence.
            </h2>
            <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '1rem' }}>
              Real-world patterns show FOIA activity correlates with major market events,
              often surging in the weeks before bankruptcies, enforcement actions, and
              corporate crises.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-3 sm:gap-5 mb-10">
            {intelStats.map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-gray-200 bg-[#FAF8F3] p-7"
              >
                <div
                  className="text-gray-900 mb-2"
                  style={{ ...headingStyle, fontSize: 'clamp(1.75rem, 3vw, 2.25rem)' }}
                >
                  {s.value}
                </div>
                <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          {/* Companies with elevated FOIA activity */}
          <div className="mb-10">
            <div className="uppercase text-gray-500 mb-4" style={labelStyle}>
              Companies with Elevated Activity
            </div>
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                {[0, 1].map((colIndex) => (
                  <div key={colIndex} className="divide-y divide-gray-100">
                    {elevatedActivity
                      .slice(colIndex * 3, (colIndex + 1) * 3)
                      .map((item) => (
                        <div
                          key={item.company}
                          className="flex items-start gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                        >
                          <div
                            className="rounded-md bg-[#FAF8F3] border border-gray-200 px-2.5 py-1 text-gray-700 flex-shrink-0"
                            style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.78rem' }}
                          >
                            {item.requests}
                          </div>
                          <div className="min-w-0">
                            <div
                              className="text-gray-900"
                              style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.9rem' }}
                            >
                              {item.company}
                            </div>
                            <p
                              className="text-gray-500 mt-0.5 leading-relaxed"
                              style={{ ...bodyStyle, fontSize: '0.78rem' }}
                            >
                              {item.context}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Who files FOIA requests */}
          <div>
            <div className="uppercase text-gray-500 mb-4" style={labelStyle}>
              Who Files SEC FOIA Requests
            </div>
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="divide-y divide-gray-100">
                {requesterMix.map((item) => (
                  <div
                    key={item.type}
                    className="flex items-center gap-5 px-5 py-3.5 hover:bg-gray-50 transition-colors"
                  >
                    <div
                      className="w-12 text-right text-gray-900"
                      style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.9rem' }}
                    >
                      {item.pct}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-gray-900"
                        style={{ ...bodyStyle, fontWeight: 400, fontSize: '0.9rem' }}
                      >
                        {item.type}
                      </div>
                      <p
                        className="text-gray-500 mt-0.5"
                        style={{ ...bodyStyle, fontSize: '0.78rem' }}
                      >
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p
              className="text-gray-400 mt-4 text-center"
              style={{ ...bodyStyle, fontSize: '0.72rem' }}
            >
              Source: Analysis of 5,219 SEC FOIA requests (6-month period).
            </p>
          </div>
        </div>
      </section>

      {/* Use cases — six tiles that invert on hover (mirrors landing page Features) */}
      <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-14 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              Applications
            </div>
            <h2
              className="text-gray-900 leading-[1.2]"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              How investment professionals use FOIA intelligence.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {useCases.map((u) => {
              const Icon = u.icon;
              return (
                <div
                  key={u.title}
                  className="group rounded-xl border border-gray-200 bg-[#FAF8F3] hover:bg-gray-900 hover:border-gray-900 p-6 sm:p-7 cursor-default"
                  style={{
                    transition:
                      'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  <div
                    className="w-11 h-11 mb-5 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-700 group-hover:bg-gray-800 group-hover:border-gray-700 group-hover:text-white"
                    style={{
                      transition:
                        'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1), color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3
                    className="text-gray-900 group-hover:text-white mb-2 leading-snug"
                    style={{
                      ...headingStyle,
                      fontWeight: 400,
                      fontSize: '1.05rem',
                      letterSpacing: '-0.01em',
                      transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                    }}
                  >
                    {u.title}
                  </h3>
                  <p
                    className="text-gray-600 group-hover:text-gray-300 leading-relaxed"
                    style={{
                      ...bodyStyle,
                      fontSize: '0.875rem',
                      transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                    }}
                  >
                    {u.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Academic sources */}
      <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10 sm:mb-12 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              Sources
            </div>
            <h2
              className="text-gray-900 leading-[1.2]"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              Academic sources.
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:gap-5">
            {papers.map((paper) => (
              <div
                key={paper.n}
                className="rounded-xl border border-gray-200 bg-[#FAF8F3] p-6 sm:p-7"
              >
                <div className="flex items-start gap-5">
                  <div
                    className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 text-gray-700"
                    style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.85rem' }}
                  >
                    {paper.n}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-gray-900 leading-snug mb-2"
                      style={{
                        ...headingStyle,
                        fontWeight: 400,
                        fontSize: '1rem',
                        letterSpacing: '-0.01em'
                      }}
                    >
                      {paper.title}
                    </h3>
                    <p
                      className="text-gray-600 mb-3"
                      style={{ ...bodyStyle, fontSize: '0.8rem' }}
                    >
                      {paper.authors}
                    </p>
                    <p
                      className="text-gray-600 mb-4 leading-relaxed"
                      style={{ ...bodyStyle, fontSize: '0.85rem' }}
                    >
                      {paper.summary}
                    </p>
                    <a
                      href={paper.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-gray-900 border-b border-gray-900 hover:border-gray-500 hover:text-gray-500 pb-0.5 uppercase transition-colors"
                      style={ctaStyle}
                    >
                      Read on SSRN
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h3
            className="text-gray-900 mb-5 leading-[1.2]"
            style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
          >
            Start tracking SEC FOIA activity.
          </h3>
          <p className="text-gray-600 mb-8 leading-relaxed" style={{ ...bodyStyle, fontSize: '1rem' }}>
            Search our database of FOIA requests and B7A exemptions, and set up alerts for
            your portfolio companies.
          </p>
          <Link
            to="/sectrace"
            className="inline-flex items-center gap-2 text-gray-900 border-b border-gray-900 hover:border-gray-500 hover:text-gray-500 pb-0.5 uppercase transition-colors"
            style={ctaStyle}
          >
            Search The Database
            <span className="text-base">→</span>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200">
        <div
          className="max-w-5xl mx-auto px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 uppercase text-[0.6rem] sm:text-[0.68rem] tracking-[0.18em] sm:tracking-[0.28em]"
          style={{ fontFamily: INTER_STACK, fontWeight: 400 }}
        >
          <Link
            to="/sectrace"
            className="text-gray-900 leading-none hover:text-gray-600 transition-colors"
            style={{ ...SECTRACE_WORDMARK, fontSize: '1.1rem' }}
          >
            SEC Trace
          </Link>
          <p className="text-gray-400 text-center">&copy; 2026 SEC Trace. A Northlight Technologies company.</p>
        </div>
      </footer>
    </div>
  );
};

export default SecTraceResearch;
